import {
  LOGO_B64,
  SIG_B64,
  COMPANY,
  C_DARK,
  C_GREY_TEXT,
  C_GREY_BG,
  C_WHITE,
} from "./generateInspectionPDF";

const C_NAVY = [30, 58, 95];
const C_GREEN = [22, 163, 74];
const C_AMBER_TEXT = [180, 110, 0];

export async function generateServiceRequestPDF(sr, signaturesMap = {}, opts = {}) {
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210;
  const pageH = 297;
  const margin = 15;
  const contentW = pageW - margin * 2;
  let y = margin;
  let pageNum = 1;

  function addPage() {
    doc.addPage();
    pageNum++;
    y = margin;
    drawPageFooter();
  }

  function drawPageFooter() {
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_GREY_TEXT);
    doc.text(`Page ${pageNum}`, pageW / 2, pageH - 7, { align: "center" });
  }

  function checkBreak(needed) {
    if (y + needed > pageH - 18) addPage();
  }

  const toDataURL = (url) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = img.width;
        c.height = img.height;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve(c.toDataURL("image/png"));
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });

  // ── HEADER ───────────────────────────────────────────────────────────────
  const logoW = 38;
  const logoH = logoW * (211 / 500);
  doc.addImage("data:image/jpeg;base64," + LOGO_B64, "JPEG", margin, y, logoW, logoH);

  const cx = pageW - margin;
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_DARK);
  doc.text(COMPANY.name, cx, y + 3, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...C_GREY_TEXT);
  doc.text(COMPANY.accreditation, cx, y + 7.5, { align: "right" });
  doc.text(COMPANY.address, cx, y + 11.5, { align: "right" });
  doc.text(COMPANY.phone, cx, y + 15.5, { align: "right" });
  doc.text(COMPANY.email, cx, y + 19.5, { align: "right" });

  y += logoH + 7;
  doc.setDrawColor(...C_GREY_TEXT);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageW - margin, y);
  y += 8;

  // ── TITLE ────────────────────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_NAVY);
  doc.text("Service Request Report", pageW / 2, y, { align: "center" });
  y += 5;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text(`Reference: SR-${String(sr.id).padStart(3, "0")}`, pageW / 2, y, { align: "center" });
  y += 7;

  // ── DETAILS BOX ──────────────────────────────────────────────────────────
  doc.setFillColor(...C_GREY_BG);
  doc.roundedRect(margin, y, contentW, 20, 2, 2, "F");
  const col1 = margin + 3;
  const col2 = margin + contentW / 2 + 3;
  const labelH = 4;

  const techName = sr.assigned_to_name || "";
  const dateCompleted = sr.completed_at
    ? new Date(sr.completed_at).toLocaleDateString("en-AU")
    : "—";
  const detailRows = [
    ["Site Name",    sr.site_name || "—",   "Technician",     techName],
    ["Site Address", sr.site_address || "—", "Date Completed", dateCompleted],
  ];

  let dy = y + 5;
  detailRows.forEach(([l1, v1, l2, v2]) => {
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_GREY_TEXT);
    doc.text(l1.toUpperCase(), col1, dy);
    doc.text(l2.toUpperCase(), col2, dy);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_DARK);
    doc.setFontSize(8.5);
    doc.text(String(v1), col1, dy + labelH);
    doc.text(String(v2), col2, dy + labelH);
    dy += 9;
  });
  y += 24;

  // ── SERVICE REQUEST BOX ───────────────────────────────────────────────────
  if (sr.service_request) {
    checkBreak(20);
    const srLines = doc.splitTextToSize(sr.service_request, contentW - 6);
    const srBoxH = srLines.length * 4.5 + 12;
    doc.setFillColor(255, 247, 237);
    doc.roundedRect(margin, y, contentW, srBoxH, 2, 2, "F");
    doc.setDrawColor(180, 110, 0);
    doc.setLineWidth(0.4);
    doc.rect(margin, y, contentW, srBoxH, "S");
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_AMBER_TEXT);
    doc.text("SERVICE REQUEST", margin + 3, y + 5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...C_DARK);
    doc.text(srLines, margin + 3, y + 10);
    y += srBoxH + 5;
  }

  // ── ACTIONS TAKEN BOX ────────────────────────────────────────────────────
  checkBreak(20);
  const actionsText = sr.actions_taken || "—";
  const actLines = doc.splitTextToSize(actionsText, contentW - 6);
  const actBoxH = actLines.length * 4.5 + 12;
  doc.setFillColor(240, 253, 244);
  doc.roundedRect(margin, y, contentW, actBoxH, 2, 2, "F");
  doc.setDrawColor(...C_GREEN);
  doc.setLineWidth(0.4);
  doc.rect(margin, y, contentW, actBoxH, "S");
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_GREEN);
  doc.text("ACTIONS TAKEN", margin + 3, y + 5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...C_DARK);
  doc.text(actLines, margin + 3, y + 10);
  y += actBoxH + 5;

  // ── COMPLETION PHOTO ─────────────────────────────────────────────────────
  if (sr.photo_url) {
    checkBreak(30);
    const dataUrl = await toDataURL(sr.photo_url);
    if (dataUrl) {
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C_GREY_TEXT);
      doc.text("COMPLETION PHOTO", margin, y);
      y += 4;
      const maxPhotoH = 60;
      const maxPhotoW = contentW;
      try {
        doc.addImage(dataUrl, "PNG", margin, y, maxPhotoW, maxPhotoH, undefined, "FAST");
      } catch (e) {}
      y += maxPhotoH + 5;
    }
  }

  // ── DECLARATION ──────────────────────────────────────────────────────────
  checkBreak(24);
  y += 4;
  doc.setFillColor(240, 253, 244);
  doc.roundedRect(margin, y, contentW, 16, 2, 2, "F");
  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...C_DARK);
  const declaration = doc.splitTextToSize(
    `I, ${techName || "the undersigned technician"}, certify that the service request described above has been completed in accordance with the relevant Australian Standards and that the information provided is true and correct.`,
    contentW - 6
  );
  doc.text(declaration, margin + 3, y + 5);
  y += 20;

  // ── SIGNATURE BLOCK ───────────────────────────────────────────────────────
  checkBreak(38);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(margin, y, margin + contentW, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_DARK);
  doc.text("Completed By", margin, y);
  y += 7;

  const sigW = (contentW - 10) / 3;
  const sx0 = margin;
  const sx1 = margin + sigW + 5;
  const sx2 = margin + (sigW + 5) * 2;
  const dateStr = new Date().toLocaleDateString("en-AU");

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text("TECHNICIAN NAME", sx0, y);
  doc.text("DATE", sx1, y);
  doc.text("SIGNATURE", sx2, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...C_DARK);
  doc.text(techName, sx0, y + 5);
  doc.text(dateStr, sx1, y + 5);

  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.4);
  doc.line(sx0, y + 6, sx0 + sigW, y + 6);
  doc.line(sx1, y + 6, sx1 + sigW, y + 6);

  const techSigB64 = signaturesMap[techName] || SIG_B64;
  const sigImgW = sigW;
  const sigImgH = sigImgW * (211 / 500);
  try {
    doc.addImage("data:image/png;base64," + techSigB64, "PNG", sx2, y - sigImgH + 8, sigImgW, sigImgH);
  } catch (e) {}
  doc.line(sx2, y + 6, sx2 + sigW, y + 6);

  drawPageFooter();

  const siteName = (sr.site_name || "site").replace(/[^a-z0-9]/gi, "_");
  if (!opts.noDownload) {
    doc.save(`SR-${String(sr.id).padStart(3, "0")}_${siteName}_Service_Request_Report.pdf`);
  }
  return doc.output("blob");
}
